/**
 * Pacific JDM theme scripts.
 * Vanilla JS, no dependencies. Behaviour is attached with data-attributes and
 * event delegation so it survives section re-renders in the theme editor.
 */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------
   * Cart
   * ---------------------------------------------------------------- */
  const cart = {
    get drawer() {
      return $('[data-cart-drawer]');
    },

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', Number(count) === 0);
      });
    },

    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },

    async refresh() {
      const drawer = this.drawer;
      if (!drawer) return;
      const response = await fetch(`${theme.routes.root}?sections=cart-drawer`);
      const sections = await response.json();
      const next = $('.cart-drawer__inner', parseHTML(sections['cart-drawer']));
      const current = $('.cart-drawer__inner', drawer);
      if (!next || !current) return;
      current.replaceWith(next);
      this.updateCount(next.dataset.cartItemCount);
    },

    open() {
      const drawer = this.drawer;
      if (!drawer) return false;
      drawer.__returnFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const close = $('button[data-cart-close]', drawer);
      if (close) close.focus();
      return true;
    },

    close() {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (drawer.__returnFocus) drawer.__returnFocus.focus();
    },

    async changeLine(line, quantity) {
      const drawer = this.drawer;
      const inDrawer = drawer && drawer.classList.contains('is-open');
      if (inDrawer) drawer.classList.add('is-loading');
      try {
        await this.post(theme.routes.cartChange, { line: Number(line), quantity: Number(quantity) });
        if (inDrawer) {
          await this.refresh();
        } else {
          window.location.reload();
        }
      } catch (error) {
        window.alert(error.message);
      } finally {
        if (drawer) drawer.classList.remove('is-loading');
      }
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target;

    const openCart = target.closest('[data-cart-open]');
    if (openCart && cart.drawer) {
      event.preventDefault();
      cart.open();
      cart.refresh();
      return;
    }

    if (target.closest('[data-cart-close]')) {
      cart.close();
      return;
    }

    const qtyChange = target.closest('[data-qty-change]');
    if (qtyChange) {
      event.preventDefault();
      const line = qtyChange.closest('[data-line]');
      if (line) cart.changeLine(line.dataset.line, qtyChange.dataset.qtyChange);
      return;
    }

    const step = target.closest('[data-qty-step]');
    if (step) {
      const input = $('input', step.parentElement);
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.qtyStep));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-qty-input]');
    if (!input) return;
    const line = input.closest('[data-line]');
    if (line) cart.changeLine(line.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* ------------------------------------------------------------------
   * Escape key + focus trap for overlays
   * ---------------------------------------------------------------- */
  document.addEventListener('keydown', (event) => {
    const drawer = cart.drawer;
    const drawerOpen = drawer && drawer.classList.contains('is-open');

    if (event.key === 'Escape') {
      if (drawerOpen) cart.close();
      closeMobileNav();
    }

    if (event.key === 'Tab' && drawerOpen) {
      const focusable = $$('a[href], button:not([disabled]), input, select, textarea', drawer).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  /* ------------------------------------------------------------------
   * Mobile navigation
   * ---------------------------------------------------------------- */
  function closeMobileNav() {
    const toggle = $('[data-nav-toggle]');
    const nav = $('#MobileNav');
    if (!toggle || !nav || nav.hidden) return;
    nav.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-nav-toggle]');
    if (!toggle) return;
    const nav = $('#MobileNav');
    if (!nav) return;
    const open = nav.hidden;
    nav.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  });

  window.matchMedia('(min-width: 990px)').addEventListener('change', (event) => {
    if (event.matches) closeMobileNav();
  });

  /* ------------------------------------------------------------------
   * Search modal
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const modal = $('[data-search-modal]');
    if (!modal) return;

    if (event.target.closest('[data-search-open]')) {
      modal.showModal();
      const input = $('input[type="search"]', modal);
      if (input) input.focus();
    } else if (event.target.closest('[data-search-close]') || event.target === modal) {
      modal.close();
    }
  });

  /* ------------------------------------------------------------------
   * Product page
   * ---------------------------------------------------------------- */
  const productSections = new WeakMap();

  function getProductState(section) {
    if (!productSections.has(section)) {
      const json = $('[data-product-json]', section);
      productSections.set(section, { variants: json ? JSON.parse(json.textContent) : [] });
    }
    return productSections.get(section);
  }

  async function onVariantChange(section) {
    const { variants } = getProductState(section);
    const fieldsets = $$('.variant-fieldset', section);

    const selected = fieldsets.map((fieldset) => {
      const checked = $('input:checked', fieldset);
      const label = $('[data-option-selected]', fieldset);
      if (label && checked) label.textContent = checked.value;
      return checked ? checked.value : null;
    });

    const variant = variants.find((v) => v.options.every((value, index) => value === selected[index]));
    const button = $('[data-add-to-cart]', section);
    const error = $('[data-product-error]', section);
    if (error) error.hidden = true;

    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = theme.strings.unavailable;
      }
      return;
    }

    const idInput = $('[data-variant-id]', section);
    if (idInput) idInput.value = variant.id;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Show only the selected color's images, then bring the variant's image into view.
    const filtered = filterGalleryByColor(section, fieldsets);
    if (!filtered && variant.featured_media) {
      const item = $(`[data-media-id="${variant.featured_media.id}"]`, section);
      if (item && !item.hidden) item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    // Refresh price and buy buttons from the server so money formatting stays correct.
    try {
      const response = await fetch(`${section.dataset.productUrl}?variant=${variant.id}&section_id=${section.dataset.sectionId}`);
      const doc = parseHTML(await response.text());
      const quantity = $('.quantity__input', section);
      const quantityValue = quantity ? quantity.value : null;

      ['[data-price]', '[data-buy]'].forEach((selector) => {
        const current = $(selector, section);
        const next = $(selector, doc);
        if (current && next) current.innerHTML = next.innerHTML;
      });

      const newQuantity = $('.quantity__input', section);
      if (newQuantity && quantityValue) newQuantity.value = quantityValue;

      // Keep the sticky add to cart bar in sync.
      const stickyPrice = $('[data-sticky-price]', section);
      const nextStickyPrice = $('[data-sticky-price]', doc);
      if (stickyPrice && nextStickyPrice) stickyPrice.innerHTML = nextStickyPrice.innerHTML;
      const stickyButton = $('[data-sticky-button]', section);
      const nextStickyButton = $('[data-sticky-button]', doc);
      if (stickyButton && nextStickyButton) {
        stickyButton.disabled = nextStickyButton.disabled;
        stickyButton.textContent = nextStickyButton.textContent;
      }
    } catch (e) {
      /* Network failure: leave the previous state in place. */
    }
  }

  /**
   * Hides gallery images that belong to a different color than the one selected.
   * Images with no color (data-color="") always show. Returns true if a filter was applied.
   */
  function filterGalleryByColor(section, fieldsets) {
    const colorFieldset = fieldsets.find((fieldset) => {
      const input = $('input', fieldset);
      return input && /^colou?r$/i.test(input.name.trim());
    });
    const gallery = $('[data-gallery]', section);
    if (!colorFieldset || !gallery) return false;

    const checked = $('input:checked', colorFieldset);
    const color = checked ? checked.value : '';
    const items = $$('.gallery__item', gallery);
    const hasMatch = items.some((item) => item.dataset.color === color);
    const visible = [];

    items.forEach((item) => {
      const hide = hasMatch && Boolean(item.dataset.color) && item.dataset.color !== color;
      item.hidden = hide;
      item.classList.remove('is-lead');
      if (!hide) visible.push(item);
    });
    if (visible.length > 1) visible[0].classList.add('is-lead');
    if (hasMatch) gallery.scrollLeft = 0;
    return hasMatch;
  }

  /* ------------------------------------------------------------------
   * Image lightbox with zoom
   * ---------------------------------------------------------------- */
  const lightbox = {
    dialog: null,
    items: [],
    index: 0,

    open(section, trigger) {
      const dialog = $('[data-lightbox]', section);
      if (!dialog || !dialog.showModal) return;
      const triggers = $$('.gallery__item:not([hidden]) [data-zoom-src]', section);
      this.dialog = dialog;
      this.items = triggers.map((button) => {
        const img = $('img', button);
        return { src: button.dataset.zoomSrc, alt: button.dataset.zoomAlt || '', thumb: img ? img.currentSrc || img.src : button.dataset.zoomSrc };
      });
      this.index = Math.max(0, triggers.indexOf(trigger));
      this.returnFocus = trigger;

      const thumbs = $('[data-lightbox-thumbs]', dialog);
      thumbs.innerHTML = '';
      this.items.forEach((item, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lightbox__thumb';
        button.dataset.lightboxThumb = i;
        button.setAttribute('aria-label', `${i + 1} / ${this.items.length}`);
        const img = document.createElement('img');
        img.src = item.thumb;
        img.alt = '';
        button.appendChild(img);
        thumbs.appendChild(button);
      });
      dialog.classList.toggle('is-single', this.items.length < 2);
      dialog.showModal();
      this.show(this.index);
    },

    show(index) {
      const count = this.items.length;
      if (!count) return;
      this.index = (index + count) % count;
      const item = this.items[this.index];
      const dialog = this.dialog;
      const image = $('[data-lightbox-image]', dialog);
      this.setZoom(false);
      image.src = item.src;
      image.alt = item.alt;
      $('[data-lightbox-counter]', dialog).textContent = `${this.index + 1} / ${count}`;
      $$('[data-lightbox-thumb]', dialog).forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        if (i === this.index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    },

    setZoom(zoomed, event) {
      const stage = $('[data-lightbox-stage]', this.dialog);
      if (!stage) return;
      let fx = 0.5;
      let fy = 0.5;
      if (zoomed && event) {
        const rect = stage.getBoundingClientRect();
        fx = (event.clientX - rect.left) / rect.width;
        fy = (event.clientY - rect.top) / rect.height;
      }
      const image = $('[data-lightbox-image]', stage);
      // Zoom relative to the size the image is displayed at when fitted to the stage.
      if (zoomed) {
        const natural = (image.naturalWidth || 1) / (image.naturalHeight || 1);
        const fitted = Math.min(stage.clientWidth, stage.clientHeight * natural);
        image.style.width = `${Math.round(fitted * 2.5)}px`;
      } else {
        image.style.width = '';
      }
      stage.classList.toggle('is-zoomed', zoomed);
      if (zoomed) this.pan(stage, fx, fy);
    },

    pan(stage, fx, fy) {
      stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) * Math.min(1, Math.max(0, fx));
      stage.scrollTop = (stage.scrollHeight - stage.clientHeight) * Math.min(1, Math.max(0, fy));
    },

    close() {
      if (!this.dialog) return;
      this.setZoom(false);
      this.dialog.close();
      if (this.returnFocus) this.returnFocus.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-zoom-src]');
    if (trigger) {
      const section = trigger.closest('[data-product-section]');
      if (section) lightbox.open(section, trigger);
      return;
    }

    const dialog = event.target.closest('[data-lightbox]');
    if (!dialog) return;
    if (event.target.closest('[data-lightbox-close]') || event.target === dialog) return lightbox.close();
    if (event.target.closest('[data-lightbox-prev]')) return lightbox.show(lightbox.index - 1);
    if (event.target.closest('[data-lightbox-next]')) return lightbox.show(lightbox.index + 1);
    const thumb = event.target.closest('[data-lightbox-thumb]');
    if (thumb) return lightbox.show(Number(thumb.dataset.lightboxThumb));
    const stage = event.target.closest('[data-lightbox-stage]');
    if (stage) lightbox.setZoom(!stage.classList.contains('is-zoomed'), event);
  });

  // With a mouse, the zoomed image follows the pointer (like a magnifier).
  document.addEventListener('mousemove', (event) => {
    const stage = event.target.closest && event.target.closest('[data-lightbox-stage].is-zoomed');
    if (!stage || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const rect = stage.getBoundingClientRect();
    lightbox.pan(stage, (event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height);
  });

  document.addEventListener('keydown', (event) => {
    if (!lightbox.dialog || !lightbox.dialog.open) return;
    if (event.key === 'ArrowLeft') lightbox.show(lightbox.index - 1);
    if (event.key === 'ArrowRight') lightbox.show(lightbox.index + 1);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('.pill__input')) return;
    const section = event.target.closest('[data-product-section]');
    if (section) onVariantChange(section);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();

    const button = $('[data-add-to-cart]', form);
    const error = $('[data-product-error]', form);
    if (!button || button.disabled) return;
    if (error) error.hidden = true;

    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || theme.strings.error);

      if (theme.cartType === 'drawer' && cart.drawer) {
        await cart.refresh();
        cart.open();
      } else {
        window.location.href = theme.routes.cart;
        return;
      }
    } catch (e) {
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  });

  /* ------------------------------------------------------------------
   * Sticky add to cart (shown once the main button scrolls out of view)
   * ---------------------------------------------------------------- */
  function updateStickyAtc() {
    $$('[data-sticky-atc]').forEach((bar) => {
      const section = bar.closest('[data-product-section]');
      const buy = section && $('[data-buy]', section);
      if (!buy) return;
      bar.classList.toggle('is-visible', buy.getBoundingClientRect().bottom < 0);
    });
  }

  function initStickyAtc() {
    if (!$('[data-sticky-atc]')) return;
    window.addEventListener('scroll', updateStickyAtc, { passive: true });
    window.addEventListener('resize', updateStickyAtc);
    updateStickyAtc();
  }

  /* ------------------------------------------------------------------
   * Dialogs (size guide)
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && dialog.showModal) dialog.showModal();
      return;
    }
    const dialog = event.target.closest('dialog.modal');
    if (dialog && (event.target === dialog || event.target.closest('[data-dialog-close]'))) dialog.close();
  });

  /* ------------------------------------------------------------------
   * Collection "Load more"
   * ---------------------------------------------------------------- */
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link) return;
    const grid = $('[data-load-more-grid]');
    const container = link.closest('[data-load-more-container]');
    if (!grid || !container) return;
    event.preventDefault();
    link.setAttribute('aria-busy', 'true');
    link.style.pointerEvents = 'none';
    try {
      const response = await fetch(link.href);
      const doc = parseHTML(await response.text());
      const nextGrid = $('[data-load-more-grid]', doc);
      const nextContainer = $('[data-load-more-container]', doc);
      if (!nextGrid || !nextContainer) throw new Error('Missing grid');
      const firstNew = nextGrid.firstElementChild;
      grid.append(...nextGrid.children);
      container.replaceWith(nextContainer);
      const focusTarget = firstNew && $('.card__title a', firstNew);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    } catch (e) {
      window.location.href = link.href;
    }
  });

  /* ------------------------------------------------------------------
   * Product recommendations (lazy loaded)
   * ---------------------------------------------------------------- */
  function loadRecommendations(container) {
    fetch(container.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const next = $('[data-recommendations]', parseHTML(html));
        if (next && next.innerHTML.trim().length) container.innerHTML = next.innerHTML;
      })
      .catch(() => {});
  }

  const recommendationObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          loadRecommendations(entry.target);
        });
      }, { rootMargin: '300px' })
    : null;

  function initRecommendations() {
    $$('[data-recommendations]').forEach((container) => {
      if (container.dataset.loaded) return;
      container.dataset.loaded = 'true';
      if (recommendationObserver) recommendationObserver.observe(container);
      else loadRecommendations(container);
    });
  }

  /* ------------------------------------------------------------------
   * Collection filters
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-filters-toggle]');
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;
    const open = panel.hidden;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
  });

  // Tag quick filters: keep the current sort and filters when switching tags.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-tag-link]');
    if (!link) return;
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    const query = params.toString();
    if (query) link.href = `${link.pathname}?${query}`;
  });

  let filterTimer;
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-auto-submit]');
    if (!input || !input.form) return;
    clearTimeout(filterTimer);
    filterTimer = setTimeout(() => input.form.requestSubmit(), input.type === 'number' ? 600 : 0);
  });

  /* ------------------------------------------------------------------
   * Customer account helpers
   * ---------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const recover = event.target.closest('[data-recover-toggle]');
    if (recover) {
      event.preventDefault();
      const panel = $('#recover');
      if (panel) panel.hidden = !panel.hidden;
      return;
    }

    const remove = event.target.closest('[data-address-delete]');
    if (remove) {
      if (!window.confirm(remove.dataset.confirm)) return;
      const form = document.createElement('form');
      form.method = 'post';
      form.action = remove.dataset.url;
      const method = document.createElement('input');
      method.type = 'hidden';
      method.name = '_method';
      method.value = 'delete';
      form.appendChild(method);
      document.body.appendChild(form);
      form.submit();
    }
  });

  /* ------------------------------------------------------------------
   * Init (+ theme editor events)
   * ---------------------------------------------------------------- */
  function init() {
    initRecommendations();
    initStickyAtc();
    if (window.location.hash === '#recover') {
      const panel = $('#recover');
      if (panel) panel.hidden = false;
    }
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
